import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logoutRequest } from "../store/actions/authActions";
import SidebarItem from "./SidebarItem";
import SidebarDropdown, { SidebarDropdownItem } from "./SidebarDropdown";
import Button from "./common/Button";
import ActiveWorkflowsSidebar from "./sidebar/ActiveWorkflowsSidebar";
import { FiBox, FiLogOut, FiTarget, FiFolder, FiFileText } from "react-icons/fi";
import {
  AiOutlineDotChart,
  AiFillPieChart,
  AiOutlineFolder,
} from "react-icons/ai";
import {
  BsFillPersonVcardFill,
  BsDiagram3,
  BsFillPeopleFill,
  BsFillGearFill,
} from "react-icons/bs";
import { RiFlowChart } from "react-icons/ri";
import { BsBug } from "react-icons/bs";
import supabaseClient from "../lib/supabaseClient";
import { FaPaperPlane } from "react-icons/fa";

const PaperPlaneIcon = (): JSX.Element =>
  React.createElement(FaPaperPlane as React.ElementType, {
    className: "su-icon-18 ml-2",
    "aria-hidden": true,
  });

const PieChartIcon = (): JSX.Element =>
  React.createElement(AiFillPieChart as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const MailIcon = (): JSX.Element =>
  React.createElement(AiOutlineFolder as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const BoxIcon = (): JSX.Element =>
  React.createElement(FiBox as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const DotChartIcon = (): JSX.Element =>
  React.createElement(AiOutlineDotChart as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const ProfileIcon = (): JSX.Element =>
  React.createElement(BsFillPersonVcardFill as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const ProfileSmallIcon = (): JSX.Element =>
  React.createElement(BsFillPersonVcardFill as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const LogOutIcon = (): JSX.Element =>
  React.createElement(FiLogOut as React.ElementType, {
    className: "mr-3 text-2xl transform -scale-x-100",
    "aria-hidden": true,
  });
const IntegrationsIcon = (): JSX.Element =>
  React.createElement(BsDiagram3 as React.ElementType, {
    className: "mr-3 text-2xl transform -scale-x-100",
    "aria-hidden": true,
  });
const ContactsIcon = (): JSX.Element =>
  React.createElement(BsFillPeopleFill as React.ElementType, {
    className: "mr-3 text-2xl transform -scale-x-100",
    "aria-hidden": true,
  });
const SettingsIcon = (): JSX.Element =>
  React.createElement(BsFillGearFill as React.ElementType, {
    className: "mr-3 text-2xl transform -scale-x-100",
    "aria-hidden": true,
  });
const WorkflowIcon = (): JSX.Element =>
  React.createElement(RiFlowChart as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const BugIcon = (): JSX.Element =>
  React.createElement(BsBug as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const FundraiseIcon = (): JSX.Element =>
  React.createElement(FiTarget as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const DataRoomsIcon = (): JSX.Element =>
  React.createElement(FiFolder as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });
const DecksIcon = (): JSX.Element =>
  React.createElement(FiFileText as React.ElementType, {
    className: "mr-3 text-2xl",
    "aria-hidden": true,
  });

interface SidebarProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isMobile = false,
  isOpen = true,
  onClose,
}) => {
  const dispatch = useDispatch();
  const handleLogout = () => {
    supabaseClient.auth.signOut().then(() => {
      dispatch(logoutRequest());
    });
  };

  const handleItemClick = () => {
    // Close mobile sidebar when navigating
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <aside
      className={`
        ${isMobile ? "w-80 h-screen" : "su-sidebar su-w-sidebar"}
        flex flex-col overflow-y-auto su-bg-n-1
      `}
      aria-label="Primary navigation sidebar"
    >
      {/* Logo */}
      <div className="px-4 pt-6 pb-5">
        <div className="flex items-center">
          <img
            src="/assets/sendupdatesai-logo.svg"
            alt="SendUpdatesAI"
            className="h-8 invert"
          />
        </div>
      </div>
      {/* Mobile close button */}
      {isMobile && (
        <div className="flex justify-between items-center mb-6 px-4">
          <h2 className="text-lg font-semibold text-white">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10"
            aria-label="Close menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      <nav className="flex flex-col flex-1 overflow-y-auto" role="menu">
        {/* Navigation Section Header */}
        <div className="pl-4 pr-4 su-section-label">Navigation</div>

        <SidebarItem to="/" exact onClick={handleItemClick}>
          <PieChartIcon /> Home
        </SidebarItem>
        <SidebarItem to="/insights" exact onClick={handleItemClick}>
          <DotChartIcon /> Insights
        </SidebarItem>
        <SidebarDropdown
          icon={<MailIcon />}
          label="Email"
          basePath="/email-updates"
          onItemClick={handleItemClick}
        >
          <SidebarDropdownItem to="/email-updates?tab=workflows" label="Workflows" />
          <SidebarDropdownItem to="/email-updates?tab=templates" label="Templates" />
          <SidebarDropdownItem to="/email-updates?tab=drafts" label="Drafts" />
        </SidebarDropdown>
        <SidebarItem to="/fundraise" exact onClick={handleItemClick}>
          <FundraiseIcon /> Fundraise
        </SidebarItem>
        <SidebarItem to="/data-rooms" onClick={handleItemClick}>
          <DataRoomsIcon /> Data Rooms
        </SidebarItem>
        <SidebarItem to="/decks" onClick={handleItemClick}>
          <DecksIcon /> Decks
        </SidebarItem>
        <SidebarItem to="/integrations" onClick={handleItemClick}>
          <IntegrationsIcon /> Integrations
        </SidebarItem>
        <SidebarItem to="/settings" onClick={handleItemClick}>
          <SettingsIcon /> Settings
        </SidebarItem>

        {/* Active Workflows Section */}
        <ActiveWorkflowsSidebar />
      </nav>

      {/* Bottom sticky profile section */}
      <div className="mt-auto pt-6">
        <SidebarItem to="/profile" onClick={handleItemClick}>
          <ProfileSmallIcon /> Profile
        </SidebarItem>
        <div className="flex-shrink-0 mt-2">
          <button
            onClick={() => {
              handleLogout();
              handleItemClick();
            }}
            className="su-menu-link w-full text-left"
            aria-label="Logout"
            type="button"
          >
            <LogOutIcon /> Logout
          </button>
        </div>
        {process.env.REACT_APP_BUILD_HASH && (
          <div className="su-version-label">#{process.env.REACT_APP_BUILD_HASH}</div>
        )}
      </div>

    </aside>
  );
};

export default Sidebar;
